/**
 * EIR view of assigned founders' fundraising plans. Lists each founder's raise
 * target / check size / stage / submission status; opening one loads the full
 * plan (getFounderFundraising) plus a Notes tab in a slide panel and renders the
 * generated 12-month roadmap via buildPlanStages.
 *
 * Auto-opens a founder via location.state.openFounderId. EIR-only: other roles
 * see a placeholder.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyFounders, getFounderFundraising } from '../services/apiService';
import SlidePanel from '../components/SlidePanel';
import NotesTab from '../components/NotesTab';
import { USE_OF_FUNDS_OPTIONS, calcMetrics, fmt, fmtMoney, buildPlanStages, planStyles as ps } from '../utils/fundraisingPlanUtils';

const USE_OF_FUNDS_LABELS = {
  product:    'Product Development',
  hiring:     'Team & Hiring',
  marketing:  'Marketing & Sales',
  operations: 'Operations',
  rd:         'R&D',
};

/** EIR overview of assigned founders' fundraising plans with a detail panel. */
export default function EIRFundraising() {
  const { user } = useAuth();
  const location = useLocation();

  const [founders, setFounders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Slide panel
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelFounder, setPanelFounder] = useState(null);
  const [planDetail, setPlanDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [panelTab, setPanelTab] = useState('overview');

  useEffect(() => {
    getMyFounders()
      .then(setFounders)
      .finally(() => setLoading(false));
  }, []);

  const openFounder = useCallback((founder) => {
    setPanelFounder(founder);
    setPanelOpen(true);
    setPanelTab('overview');
    setPlanDetail(null);
    setDetailLoading(true);
    getFounderFundraising(founder.id)
      .then(setPlanDetail)
      .finally(() => setDetailLoading(false));
  }, []);

  useEffect(() => {
    if (loading || !founders.length) return;
    const id = location.state?.openFounderId;
    if (!id) return;
    const founder = founders.find((f) => f.id === id);
    if (founder) openFounder(founder);
  }, [loading, founders]); // openFounder is stable (useCallback)

  if (user?.role !== 'eir') {
    return (
      <div style={styles.page}>
        <div style={styles.inner}>
          <p style={styles.dim}>This view is for EIRs. Use the Admin dashboard to review founder fundraising plans.</p>
        </div>
      </div>
    );
  }

  const withPlan = founders.filter((f) => f.fundraising).length;
  const totalRaise = founders
    .filter((f) => f.fundraising?.raise_amount)
    .reduce((s, f) => s + f.fundraising.raise_amount, 0);

  return (
    <div style={styles.page}>
      <div style={styles.inner}>

        <div style={styles.header}>
          <h1 style={styles.title}>Fundraising Plans</h1>
          <p style={styles.subtitle}>
            Review your founders' fundraising readiness and submitted plans.
          </p>
        </div>

        {/* Stats */}
        {!loading && founders.length > 0 && (
          <div style={styles.statsRow}>
            <StatCard label="Founders" value={founders.length} />
            <StatCard label="Plans Submitted" value={withPlan} />
            {totalRaise > 0 && (
              <StatCard label="Total Raise Target" value={`$${(totalRaise / 1000000).toFixed(1)}M`} />
            )}
          </div>
        )}

        {/* Founder list */}
        {loading && <p style={styles.dim}>Loading…</p>}
        {!loading && founders.length === 0 && (
          <p style={styles.dim}>No founders assigned yet.</p>
        )}
        {!loading && founders.length > 0 && (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 'var(--radius-lg)' }}>
          <div style={{ ...styles.table, minWidth: 620 }}>
            <div style={styles.tableHead}>
              <span style={{ ...styles.col, flex: 2 }}>Founder</span>
              <span style={{ ...styles.col, flex: 1.5 }}>Raise Target</span>
              <span style={{ ...styles.col, flex: 1.5 }}>Min. Check</span>
              <span style={{ ...styles.col, flex: 1 }}>Stage</span>
              <span style={{ ...styles.col, flex: 1 }}>Status</span>
            </div>
            {founders.map((f) => {
              const plan = f.fundraising;
              return (
                <button key={f.id} style={styles.tableRow} onClick={() => openFounder(f)}>
                  <div style={{ flex: 2, minWidth: 0 }}>
                    <p style={styles.rowName}>{f.first_name} {f.last_name}</p>
                    {f.company_name && <p style={styles.rowCompany}>{f.company_name}</p>}
                  </div>
                  <span style={{ flex: 1.5, fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)' }}>
                    {plan?.raise_amount ? fmtAmount(plan.raise_amount) : '—'}
                  </span>
                  <span style={{ flex: 1.5, fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)' }}>
                    {plan?.min_check_size ? fmtAmount(plan.min_check_size) : '—'}
                  </span>
                  <span style={{ flex: 1 }}>
                    {(plan?.company_stage || f.company_stage) ? (
                      <span style={{ ...styles.stagePill, ...stageColor(plan?.company_stage || f.company_stage) }}>
                        {plan?.company_stage || f.company_stage}
                      </span>
                    ) : '—'}
                  </span>
                  <span style={{ flex: 1 }}>
                    <span style={{ ...styles.statusPill, ...(plan ? styles.statusHas : styles.statusNone) }}>
                      {plan ? 'Submitted' : 'Not started'}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          </div>
        )}

      </div>

      {/* Detail slide panel */}
      <SlidePanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={panelFounder ? `${panelFounder.first_name} ${panelFounder.last_name}` : ''}
      >
        <div style={tabStyles.tabBar}>
          {['overview', 'notes'].map((t) => (
            <button key={t} style={panelTab === t ? tabStyles.tabActive : tabStyles.tab} onClick={() => setPanelTab(t)}>
              {t === 'overview' ? 'Overview' : 'Notes'}
            </button>
          ))}
        </div>
        {panelTab === 'overview' ? (
          <>
            {detailLoading && <p style={styles.dim}>Loading plan…</p>}
            {!detailLoading && !planDetail && panelFounder && <EmptyPlan founder={panelFounder} />}
            {!detailLoading && planDetail && <PlanDetail plan={planDetail} founder={panelFounder} />}
          </>
        ) : (
          panelFounder && (
            <NotesTab
              founderUserId={panelFounder.id}
              founderName={`${panelFounder.first_name} ${panelFounder.last_name}`}
              page="fundraising"
            />
          )
        )}
      </SlidePanel>
    </div>
  );
}

// Compact money formatter: $X.XXM (trailing .00 stripped) for millions, else $XK.
function fmtAmount(n) {
  if (!n && n !== 0) return '—';
  return n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(2).replace(/\.00$/, '')}M`
    : `$${Math.round(n / 1_000)}K`;
}

// Read-only render of a submitted plan: key metrics, use-of-funds tags, and the
// generated roadmap. numChecks is derived (raise / min check) when both exist.
function PlanDetail({ plan, founder }) {

  const useOfFundsList = plan.use_of_funds
    ? plan.use_of_funds.split(',').map((k) => USE_OF_FUNDS_LABELS[k.trim()] || k.trim())
    : [];

  const numChecks = (plan.raise_amount && plan.min_check_size && plan.min_check_size > 0)
    ? Math.ceil(plan.raise_amount / plan.min_check_size)
    : null;

  return (
    <div style={panelStyles.wrap}>
      <div style={panelStyles.nameRow}>
        <p style={panelStyles.company}>{founder?.company_name || `${founder?.first_name}'s Company`}</p>
        {(plan.company_stage) && (
          <span style={{ ...panelStyles.stagePill, ...stageColor(plan.company_stage) }}>
            {plan.company_stage}
          </span>
        )}
      </div>

      <div style={panelStyles.metricsGrid}>
        <Metric label="Raise Target" value={fmtAmount(plan.raise_amount)} />
        <Metric label="Min. Check Size" value={fmtAmount(plan.min_check_size)} />
        {numChecks && <Metric label="Checks Needed" value={numChecks} />}
        {plan.runway_months && <Metric label="Runway" value={`${plan.runway_months} mo.`} />}
        {plan.customer_count != null && <Metric label="Customers" value={plan.customer_count} />}
        <Metric label="Structure" value={plan.dilutive === true ? 'Dilutive' : plan.dilutive === false ? 'Non-dilutive' : '—'} />
      </div>

      {useOfFundsList.length > 0 && (
        <div style={panelStyles.section}>
          <p style={panelStyles.sectionLabel}>Use of Funds</p>
          <div style={panelStyles.tagRow}>
            {useOfFundsList.map((label, i) => (
              <span key={i} style={panelStyles.tag}>{label}</span>
            ))}
          </div>
        </div>
      )}

      {plan.updated_at && (
        <p style={panelStyles.updatedAt}>Last updated {plan.updated_at}</p>
      )}

      {/* Full generated plan stages */}
      {plan.raise_amount && plan.min_check_size && (
        <div style={{ marginTop: 8 }}>
          <p style={panelStyles.sectionLabel}>12-Month Fundraising Roadmap</p>
          {buildPlanStages(plan).map((stage, i) => (
            <div key={i} style={ps.stage}>
              <h3 style={ps.stageTitle}>{stage.title}</h3>
              <p style={ps.stageSubtitle}>{stage.subtitle}</p>
              <ul style={ps.bullets}>
                {stage.bullets.map((b, j) => (
                  <li key={j} style={ps.bullet}>{b}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyPlan({ founder }) {
  return (
    <div style={panelStyles.wrap}>
      <p style={panelStyles.company}>{founder?.company_name || `${founder?.first_name}'s Company`}</p>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.55, fontStyle: 'italic', marginTop: 12 }}>
        {founder.first_name} has not submitted a fundraising plan yet.
      </p>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div style={panelStyles.metric}>
      <p style={panelStyles.metricValue}>{value}</p>
      <p style={panelStyles.metricLabel}>{label}</p>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={styles.statCard}>
      <p style={styles.statValue}>{value}</p>
      <p style={styles.statLabel}>{label}</p>
    </div>
  );
}

function stageColor(stage) {
  const map = {
    idea:      { background: '#f0e8ff', color: '#5b21b6' },
    prototype: { background: '#fef9c3', color: '#854d0e' },
    pilot:     { background: '#e0f2fe', color: '#0369a1' },
    revenue:   { background: '#dcfce7', color: '#15803d' },
    scaling:   { background: '#fff7ed', color: '#c2410c' },
  };
  return map[stage] || { background: 'var(--color-mint-dark)', color: 'var(--color-forest)' };
}

const styles = {
  page: { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner: { maxWidth: "98%", margin: "0 auto" },
  header: { marginBottom: 'var(--space-6)', paddingTop: 'var(--space-4)' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 64, fontWeight: 500, color: 'var(--color-rust)', lineHeight: 1.1, marginBottom: 'var(--space-2)' },
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 22, fontWeight: 'var(--font-weight-page-subtitle)', color: 'var(--color-forest)', opacity: 0.6, lineHeight: 1.5 },
  dim: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.5, fontStyle: 'italic' },

  statsRow: { display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-6)', flexWrap: 'wrap', maxWidth: 800 },
  statCard: { background: 'var(--color-frost)', borderRadius: 'var(--radius-md)', padding: '16px 24px', boxShadow: 'var(--shadow-card)', flex: 1, textAlign: 'center' },
  statValue: { fontFamily: 'var(--font-accent)', fontSize: 28, fontWeight: 800, color: 'var(--color-forest)', lineHeight: 1.1, marginBottom: 4 },
  statLabel: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.06em' },

  table: { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)', overflow: 'hidden' },
  tableHead: { display: 'flex', padding: '10px 20px', background: 'var(--color-mint-dark)', gap: 16, alignItems: 'center' },
  tableRow: { display: 'flex', padding: '14px 20px', gap: 16, alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', borderBottom: '1px solid var(--color-mint)', transition: 'background 0.12s' },
  col: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.05em' },

  rowName: { fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 700, color: 'var(--color-forest)' },
  rowCompany: { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.6, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.05em' },

  stagePill: { padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: 15, fontWeight: 700, textTransform: 'capitalize' },
  statusPill: { padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' },
  statusHas: { background: '#dcfce7', color: '#15803d' },
  statusNone: { background: 'var(--color-mint-dark)', color: 'var(--color-forest)', opacity: 0.55 },
};

const tabStyles = {
  tabBar:    { display: 'flex', gap: 4, marginBottom: 16 },
  tab:       { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, padding: '6px 16px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: 'var(--color-frost)', color: 'var(--color-forest)', cursor: 'pointer' },
  tabActive: { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, padding: '6px 16px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-forest)', background: 'var(--color-forest)', color: '#fff', cursor: 'pointer' },
};

const panelStyles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' },
  nameRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  company: { fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--color-forest)' },
  stagePill: { padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: 15, fontWeight: 700, textTransform: 'capitalize', flexShrink: 0 },

  metricsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  metric: { background: 'var(--color-mint)', borderRadius: 'var(--radius-sm)', padding: '12px 16px' },
  metricValue: { fontFamily: 'var(--font-accent)', fontSize: 20, fontWeight: 800, color: 'var(--color-forest)', lineHeight: 1.1, marginBottom: 2 },
  metricLabel: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.05em' },

  section: { display: 'flex', flexDirection: 'column', gap: 8 },
  sectionLabel: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.06em' },
  tagRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  tag: { padding: '4px 12px', borderRadius: 'var(--radius-full)', background: 'var(--color-mint-dark)', fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--color-forest)' },

  updatedAt: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.4, fontStyle: 'italic' },
};
